import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  Plus,
  Search,
  Copy,
  Check,
  Trash2,
  Edit2,
  X,
  Clock,
  Users,
  AlertCircle,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';

const supabase = getSupabaseAdminClient();

type CodeType = 'beta' | 'promo';

interface BetaInvitation {
  id: string;
  code: string;
  code_type: CodeType;
  promo_months: number | null; // null = lifetime
  valid_from_redemption: boolean;
  created_by: string | null;
  used_by: string | null;
  used_at: string | null;
  expires_at: string | null;
  max_uses: number | null;
  current_uses: number;
  is_active: boolean;
  note: string | null;
  created_at: string;
  redemptions: Array<{
    user_id: string;
    redeemed_at: string;
    is_retroactive: boolean;
    profile: {
      email: string;
      display_name: string | null;
    } | null;
  }>;
}

const PROMO_MONTH_OPTIONS = [
  { value: '', label: 'Lebenslang' },
  { value: '1', label: '1 Monat' },
  { value: '3', label: '3 Monate' },
  { value: '6', label: '6 Monate' },
  { value: '12', label: '12 Monate (1 Jahr)' },
];

// Generate a random code with appropriate prefix
function generateCode(type: CodeType = 'beta'): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excluded similar chars like 0/O, 1/I
  const prefix = type === 'promo' ? 'PROMO-' : 'BETA-';
  let code = prefix;
  for (let i = 0; i < 8; i++) {
    if (i === 4) code += '-';
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// Format promo duration for display
function formatPromoDuration(months: number | null): string {
  if (months === null) return 'Lebenslang';
  if (months === 1) return '1 Monat';
  if (months === 12) return '1 Jahr';
  return `${months} Monate`;
}

interface CreateEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingCode?: BetaInvitation | null;
}

function CreateEditModal({ isOpen, onClose, existingCode }: CreateEditModalProps) {
  const queryClient = useQueryClient();
  const [codeType, setCodeType] = useState<CodeType>(existingCode?.code_type || 'beta');
  const [useCustomCode, setUseCustomCode] = useState(false);
  const [code, setCode] = useState(existingCode?.code || generateCode('beta'));
  const [note, setNote] = useState(existingCode?.note || '');
  const [maxUses, setMaxUses] = useState(existingCode?.max_uses?.toString() || '1');
  const [promoMonths, setPromoMonths] = useState(existingCode?.promo_months?.toString() || '');
  const [validFromRedemption, setValidFromRedemption] = useState(existingCode?.valid_from_redemption ?? true);
  const [expiresAt, setExpiresAt] = useState(
    existingCode?.expires_at ? existingCode.expires_at.split('T')[0] : ''
  );

  const createMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('beta_invitations')
        .insert({
          code: useCustomCode ? code.trim() : code.toUpperCase(),
          code_type: codeType,
          promo_months: codeType === 'promo' ? (promoMonths ? parseInt(promoMonths) : null) : null,
          valid_from_redemption: codeType === 'promo' ? validFromRedemption : false,
          note: note || null,
          max_uses: maxUses ? parseInt(maxUses) : null,
          expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
          created_by: user?.id || null,
          current_uses: 0,
          is_active: true,
        } as never);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(codeType === 'promo' ? 'Promo-Code erstellt' : 'Beta-Code erstellt');
      queryClient.invalidateQueries({ queryKey: ['beta-codes'] });
      onClose();
    },
    onError: (error: Error) => {
      if (error.message.includes('duplicate')) {
        toast.error('Dieser Code existiert bereits');
      } else {
        toast.error('Fehler beim Erstellen');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('beta_invitations')
        .update({
          promo_months: existingCode?.code_type === 'promo' ? (promoMonths ? parseInt(promoMonths) : null) : null,
          valid_from_redemption: existingCode?.code_type === 'promo' ? validFromRedemption : false,
          note: note || null,
          max_uses: maxUses ? parseInt(maxUses) : null,
          expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        } as never)
        .eq('id', existingCode!.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Code aktualisiert');
      queryClient.invalidateQueries({ queryKey: ['beta-codes'] });
      onClose();
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (existingCode) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const regenerateCode = () => {
    setUseCustomCode(false);
    setCode(generateCode(codeType));
  };

  const handleCodeTypeChange = (newType: CodeType) => {
    setCodeType(newType);
    if (!useCustomCode) {
      // Only regenerate code with new prefix when not using custom code
      setCode(generateCode(newType));
    }
  };

  const handleToggleCustomCode = () => {
    if (useCustomCode) {
      // Switch back to auto-generated
      setUseCustomCode(false);
      setCode(generateCode(codeType));
    } else {
      // Switch to custom
      setUseCustomCode(true);
      setCode('');
    }
  };

  if (!isOpen) return null;

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900">
              {existingCode ? 'Code bearbeiten' : 'Neuer Beta-Code'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Code Type Toggle (only for new codes) */}
          {!existingCode && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Code-Typ</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleCodeTypeChange('beta')}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    codeType === 'beta'
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Beta-Code
                </button>
                <button
                  type="button"
                  onClick={() => handleCodeTypeChange('promo')}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    codeType === 'promo'
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Promo-Code
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {codeType === 'beta'
                  ? 'Beta-Codes gewähren lebenslangen Premium-Zugang'
                  : 'Promo-Codes gewähren zeitlich begrenzten Premium-Zugang'}
              </p>
            </div>
          )}

          {/* Show code type badge for existing codes */}
          {existingCode && (
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                existingCode.code_type === 'promo'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-purple-100 text-purple-700'
              }`}>
                {existingCode.code_type === 'promo' ? 'Promo-Code' : 'Beta-Code'}
              </span>
            </div>
          )}

          {/* Code */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-slate-700">Code</label>
              {!existingCode && (
                <button
                  type="button"
                  onClick={handleToggleCustomCode}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                >
                  {useCustomCode ? 'Auto-generieren' : 'Eigenen Code eingeben'}
                </button>
              )}
            </div>
            {existingCode ? (
              <div className="px-4 py-3 bg-slate-100 rounded-lg font-mono text-lg text-center">
                {existingCode.code}
              </div>
            ) : useCustomCode ? (
              <div>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="z.B. welcomepp, 3monthfree..."
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg font-mono text-center focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Eigener Code – z.B. für Kampagnen oder individuelle Gutscheine
                </p>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg font-mono text-center focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                  required
                />
                <button
                  type="button"
                  onClick={regenerateCode}
                  className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg"
                  title="Neu generieren"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Promo-specific fields */}
          {(codeType === 'promo' || existingCode?.code_type === 'promo') && (
            <>
              {/* Promo Duration */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Premium-Dauer
                </label>
                <select
                  value={promoMonths}
                  onChange={(e) => setPromoMonths(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                >
                  {PROMO_MONTH_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Wie lange der Premium-Zugang nach Einlösung gilt
                </p>
              </div>

              {/* Valid from Redemption */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={validFromRedemption}
                      onChange={(e) => setValidFromRedemption(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-6 bg-slate-200 rounded-full peer peer-checked:bg-purple-600 transition-colors"></div>
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-4"></div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">
                      Gültigkeit ab Einlösung
                    </span>
                    <p className="text-xs text-slate-500">
                      {validFromRedemption
                        ? 'Premium-Dauer beginnt mit Einlösung des Codes'
                        : 'Premium-Dauer beginnt mit Code-Erstellung'}
                    </p>
                  </div>
                </label>
              </div>
            </>
          )}

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notiz</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z.B. Instagram Gewinnspiel, Tester XY..."
              rows={2}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none"
            />
          </div>

          {/* Max Uses */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Maximale Nutzungen
            </label>
            <input
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              min="1"
              placeholder="Unbegrenzt"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
            />
            <p className="text-xs text-slate-500 mt-1">Leer lassen für unbegrenzte Nutzungen</p>
          </div>

          {/* Expires At */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Gültig bis
            </label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
            />
            <p className="text-xs text-slate-500 mt-1">Leer lassen für unbegrenzte Gültigkeit</p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {existingCode ? 'Speichern' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function BetaCodesPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCode, setEditingCode] = useState<BetaInvitation | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [expandedCodeId, setExpandedCodeId] = useState<string | null>(null);

  // Fetch beta codes mit allen Einlösungen aus code_redemptions
  const { data: codes, isLoading } = useQuery({
    queryKey: ['beta-codes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('beta_invitations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const invitations = data as Omit<BetaInvitation, 'redemptions'>[];
      const invitationIds = invitations.map((i) => i.id);

      // Alle Einlösungen für diese Codes laden
      let redemptionsByInvitation: Record<string, BetaInvitation['redemptions']> = {};
      if (invitationIds.length > 0) {
        const { data: redemptions } = await supabase
          .from('code_redemptions')
          .select('invitation_id, user_id, redeemed_at, is_retroactive')
          .in('invitation_id', invitationIds);

        const redemptionRows = (redemptions ?? []) as Array<{
          invitation_id: string;
          user_id: string;
          redeemed_at: string;
          is_retroactive: boolean;
        }>;

        // Profile-Lookup
        const userIds = [...new Set(redemptionRows.map((r) => r.user_id))];
        let profileMap: Record<string, { email: string; display_name: string | null }> = {};
        if (userIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, email, display_name')
            .in('id', userIds);
          if (profiles) {
            profileMap = (profiles as { id: string; email: string; display_name: string | null }[])
              .reduce((acc, p) => {
                acc[p.id] = { email: p.email, display_name: p.display_name };
                return acc;
              }, {} as Record<string, { email: string; display_name: string | null }>);
          }
        }

        redemptionsByInvitation = redemptionRows.reduce((acc, r) => {
          if (!acc[r.invitation_id]) acc[r.invitation_id] = [];
          acc[r.invitation_id].push({
            user_id: r.user_id,
            redeemed_at: r.redeemed_at,
            is_retroactive: r.is_retroactive,
            profile: profileMap[r.user_id] ?? null,
          });
          return acc;
        }, {} as Record<string, BetaInvitation['redemptions']>);
      }

      return invitations.map((inv) => ({
        ...inv,
        redemptions: redemptionsByInvitation[inv.id] ?? [],
      })) as BetaInvitation[];
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('beta_invitations')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Beta-Code gelöscht');
      queryClient.invalidateQueries({ queryKey: ['beta-codes'] });
    },
    onError: (error: Error) => {
      const msg = error.message?.toLowerCase() ?? '';
      if (msg.includes('foreign key') || msg.includes('violates foreign key constraint')) {
        toast.error('Code wurde bereits eingelöst – bitte stattdessen deaktivieren');
      } else {
        toast.error('Fehler beim Löschen');
      }
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('beta_invitations')
        .update({ is_active: isActive } as never)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, { isActive }) => {
      toast.success(isActive ? 'Code aktiviert' : 'Code deaktiviert');
      queryClient.invalidateQueries({ queryKey: ['beta-codes'] });
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  const handleCopyCode = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code kopiert');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleDelete = (id: string, code: string) => {
    if (confirm(`Beta-Code "${code}" wirklich löschen?`)) {
      deleteMutation.mutate(id);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getCodeStatus = (code: BetaInvitation) => {
    if (!code.is_active) return { label: 'Deaktiviert', color: 'bg-slate-100 text-slate-600' };
    if (code.expires_at && new Date(code.expires_at) < new Date()) {
      return { label: 'Abgelaufen', color: 'bg-red-100 text-red-700' };
    }
    if (code.max_uses && code.current_uses >= code.max_uses) {
      return { label: 'Aufgebraucht', color: 'bg-amber-100 text-amber-700' };
    }
    return { label: 'Aktiv', color: 'bg-emerald-100 text-emerald-700' };
  };

  // Filter codes based on search
  const filteredCodes = codes?.filter(code => {
    const query = searchQuery.toLowerCase();
    if (!query) return true;
    return (
      code.code.toLowerCase().includes(query) ||
      code.note?.toLowerCase().includes(query) ||
      code.redemptions.some((r) =>
        r.profile?.email.toLowerCase().includes(query) ||
        r.profile?.display_name?.toLowerCase().includes(query),
      )
    );
  });

  // Stats
  const stats = {
    total: codes?.length || 0,
    beta: codes?.filter(c => c.code_type === 'beta').length || 0,
    promo: codes?.filter(c => c.code_type === 'promo').length || 0,
    used: codes?.filter(c => c.current_uses > 0).length || 0,
    redemptionsTotal: codes?.reduce((sum, c) => sum + c.redemptions.length, 0) || 0,
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Beta- & Promo-Codes</h1>
            <p className="text-slate-600">Verwalte Beta-Einladungscodes und Promo-Codes</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Neuer Code
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
              <div className="text-sm text-slate-500">Gesamt</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.beta}</div>
              <div className="text-sm text-slate-500">Beta-Codes</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.promo}</div>
              <div className="text-sm text-slate-500">Promo-Codes</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.used}</div>
              <div className="text-sm text-slate-500">Eingelöst</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.redemptionsTotal}</div>
              <div className="text-sm text-slate-500">Einlösungen gesamt</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Code, Notiz oder E-Mail suchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
        ) : filteredCodes && filteredCodes.length > 0 ? (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Code</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Typ</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Dauer</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Nutzung</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Notiz</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Eingelöst von</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Code gültig bis</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredCodes.map((code) => {
                const status = getCodeStatus(code);
                const isExpanded = expandedCodeId === code.id;
                const redemptionCount = code.redemptions.length;
                const onlyRedemption = redemptionCount === 1 ? code.redemptions[0] : null;

                return (
                  <React.Fragment key={code.id}>
                    <tr className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-slate-900">{code.code}</span>
                          <button
                            onClick={() => handleCopyCode(code.code)}
                            className="p-1 hover:bg-slate-100 rounded"
                            title="Code kopieren"
                          >
                            {copiedCode === code.code ? (
                              <Check className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <Copy className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          code.code_type === 'promo'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {code.code_type === 'promo' ? 'Promo' : 'Beta'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">
                          {code.code_type === 'promo'
                            ? formatPromoDuration(code.promo_months)
                            : 'Lebenslang'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">
                          {code.current_uses} / {code.max_uses || '∞'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600 max-w-[200px] truncate block">
                          {code.note || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {redemptionCount === 0 ? (
                          <span className="text-sm text-slate-400">-</span>
                        ) : onlyRedemption ? (
                          <div className="text-sm">
                            <div className="text-slate-900">{onlyRedemption.profile?.display_name || 'Unbenannt'}</div>
                            <div className="text-slate-500 text-xs">{onlyRedemption.profile?.email ?? '-'}</div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setExpandedCodeId(isExpanded ? null : code.id)}
                            className="text-sm text-purple-600 hover:text-purple-700 underline"
                          >
                            {redemptionCount} User {isExpanded ? '(zuklappen)' : '(anzeigen)'}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">
                          {formatDate(code.expires_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => toggleActiveMutation.mutate({ id: code.id, isActive: !code.is_active })}
                            className={`p-1.5 rounded hover:bg-slate-100 ${
                              code.is_active ? 'text-emerald-600' : 'text-slate-400'
                            }`}
                            title={code.is_active ? 'Deaktivieren' : 'Aktivieren'}
                          >
                            {code.is_active ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <AlertCircle className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => setEditingCode(code)}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                            title="Bearbeiten"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(code.id, code.code)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && redemptionCount > 1 && (
                      <tr className="bg-slate-50">
                        <td colSpan={9} className="px-4 py-3">
                          <div className="space-y-2">
                            <div className="text-xs font-semibold text-slate-600 mb-2">
                              Alle Einlösungen ({redemptionCount})
                            </div>
                            {code.redemptions.map((r) => (
                              <div
                                key={r.user_id}
                                className="flex items-center justify-between text-sm bg-white rounded p-2 border border-slate-200"
                              >
                                <div>
                                  <div className="text-slate-900">{r.profile?.display_name || 'Unbenannt'}</div>
                                  <div className="text-slate-500 text-xs">{r.profile?.email ?? '-'}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {r.is_retroactive && (
                                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                                      Nachträglich
                                    </span>
                                  )}
                                  <span className="text-xs text-slate-500">
                                    {formatDate(r.redeemed_at)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <Shield className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">
              {searchQuery ? 'Keine Codes gefunden' : 'Noch keine Beta-Codes erstellt'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Ersten Code erstellen
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateEditModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Edit Modal */}
      {editingCode && (
        <CreateEditModal
          isOpen={true}
          onClose={() => setEditingCode(null)}
          existingCode={editingCode}
        />
      )}
    </div>
  );
}
